import { friendlyError } from './utils/friendly-error.js';
import { subActionAllowed } from './data/zone-subactions.js';
import { withTimeout } from './utils/with-timeout.js';
import { auth, db, functions, fallBackToLongPolling } from './firebase.js';
import { signInWithPopup, signInWithEmailAndPassword, GoogleAuthProvider, OAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, getDocs, collection, serverTimestamp } from 'firebase/firestore';
import { ROLE_MATRIX, roleZoneAccess, roleZoneScoped } from './data/role-matrix.js';
import { resolveZoneValue } from './data/zone-aliases.js';
import { BULK_STAGES, bulkStageDef } from './data/bulk-editor-access.js';

const provider = new GoogleAuthProvider();
// No extra scopes — the gmail.send scope was retired when we moved email
// sending to a server-side Cloud Function (Resend transactional provider).
// Drive and Calendar scopes were retired earlier.
// For production: restrict to school's Workspace domain
// provider.setCustomParameters({ hd: 'yourschool.vic.edu.au' });

let currentUser = null;
let currentUserRole = 'all_teachers'; // safe default - least privilege among view roles
let currentUserPermissions = {};
let currentUserYearLevel = null; // legacy single-yearLevel field (grade_teacher role)
// New per-user scope fields. Either may be set by the admin when creating
// the user; if both are empty the user sees the cohort their role allows.
let currentUserScopeYearLevels = []; // ['Prep', 'Year 3', ...] - see only these
let currentUserScopeStudentIds = []; // ['stu_abc', ...] - see only these
let currentUserLinkedStaffId = null; // /staff id a teacher/ESO login is bound to
let currentUserSignature = null; // { image, name } - user's saved signature
let currentUserDisplayName = ''; // /users/{uid}.displayName override (defaults to Auth's displayName from Google)
let currentUserPrefs = {};       // /users/{uid}.prefs - personal UI defaults (e.g. NCCD-only filters)
const authListeners = [];

export function onAuthChange(callback) {
  authListeners.push(callback);
  if (currentUser) callback(currentUser, currentUserRole);
}

export function getCurrentUser() { return currentUser; }
export function getCurrentRole() { return currentUserRole; }
// Set when the boot-time user-doc read could not reach Firestore at all (both
// the initial attempt and the retry timed out). main.js reads this so it can
// show a truthful "can't reach the database" diagnostic instead of dropping the
// user into a half-built app. Null on a healthy boot.
let bootConnectError = null;
export function getBootConnectError() { return bootConnectError; }
// Vendor master accounts (also hardcoded in the login/bootstrap gates below).
const MASTER_EMAILS = ['harry@easyclarity4growth.com', 'elizabeth@easyclarity4growth.com'];
/** True only for the vendor master owner accounts. Used to gate the owner-only
 *  "Test as user" tool so no school-side account can impersonate real users. */
export function isMasterUser() {
  return MASTER_EMAILS.includes((currentUser?.email || '').toLowerCase());
}
/** Test-only: override the current role for unit tests. */
export function __setTestRole(role) { currentUserRole = role; }
/**
 * Owner "Test as user" impersonation (practice projects only — gated in main.js).
 * Overlays the ENTIRE client-side identity — role, custom permissions, and
 * scope — with a target user's live /users doc so every gate (sectionAccess,
 * canViewStudent, getVisibleStudents, canEdit*, the nav, the buttons) resolves
 * exactly as that user would experience it. Note: the underlying Firebase Auth
 * session is still the owner's, so this reproduces what the user SEES/CAN-DO in
 * the UI, not server-side Firestore-rule enforcement (that would require
 * signing in as them). Mirrors the fields set from the /users doc on real login. */
export function applyTestUser(data) {
  if (!data) return;
  currentUserRole = normaliseRole(data.role || 'all_teachers');
  currentUserPermissions = data.customPermissions || {};
  currentUserScopeYearLevels = Array.isArray(data.scopeYearLevels) ? data.scopeYearLevels : [];
  currentUserScopeStudentIds = Array.isArray(data.scopeStudentIds) ? data.scopeStudentIds : [];
  currentUserYearLevel = data.yearLevel || null;
  currentUserLinkedStaffId = data.linkedStaffId || null;
  currentUserDisplayName = data.displayName || '';
  currentUserPrefs = data.prefs || {};
}
export function getCurrentPermissions() { return currentUserPermissions; }
/** Personal UI preferences from /users/{uid}.prefs. */
export function getUserPrefs() { return currentUserPrefs; }
export function setUserPrefsLocal(p) { currentUserPrefs = { ...(currentUserPrefs || {}), ...(p || {}) }; }
/** Should filter bars start with "NCCD only" ticked? (My Profile setting.) */
export function nccdOnlyDefault() { return currentUserPrefs?.nccdOnlyDefault === true; }
export function getCurrentUserSignature() { return currentUserSignature; }
export function setCurrentUserSignature(sig) { currentUserSignature = sig || null; }
// Best-known display name for the signed-in user. Order of precedence:
//   1. /users/{uid}.displayName (user-edited via My Profile)
//   2. auth.currentUser.displayName (Google sign-in)
//   3. email local-part as a last-resort
export function getCurrentDisplayName() {
  if (currentUserDisplayName) return currentUserDisplayName;
  const auth = currentUser?.displayName || '';
  if (auth) return auth;
  const email = currentUser?.email || '';
  return email ? email.split('@')[0] : '';
}
export function setCurrentDisplayName(name) { currentUserDisplayName = name || ''; }

// === ROLE MODEL (9 roles) ===
//
// owner         : School owner — full access to everything, can add students, all buttons
// assistant     : Owner's assistant — full access except plans, incidents, emails; can add students
// executive     : Executive team — full access to all sections and buttons
// leadership    : Leadership — summary, notes, goals, plans, timetable only
// specialist    : Specialist teachers — summary, adjustments, plans only
// intervention  : Intervention teachers — summary, notes, profile, adjustments, goals, meetings, timetable
// grade_teacher : Class teacher — can only see students in their assigned year level; most sections
// all_teachers  : All teachers (read-only) — summary, notes only
// eso           : Education Support Officers — summary, notes, meetings only
// custom        : Granular per-section permissions (fallback, kept for backward compatibility)
//
// Backward compatibility: old 'admin' role maps to 'executive', old 'user' maps to 'all_teachers'

export const ROLE_LABELS = {
  owner: 'Owner',
  ld_leader: 'Learning Diversity Leader',
  principal: 'Principal / Deputy',
  assistant: 'Assistant',
  executive: 'Executive Team',
  leadership: 'Leadership',
  specialist: 'Specialist Teachers',
  intervention: 'Intervention Teachers',
  grade_teacher: 'Classroom Teacher (own classes)',
  all_teachers: 'General Teacher',
  eso: 'ESOs',
  custom: 'Custom',
};

// Roles with full, unrestricted access INCLUDING the admin area, data import,
// appointment setup, and the large cohort AI insights. Per the role spec only
// these three (plus the legacy executive/assistant who already had admin) sit
// at the top tier. Keep owner first.
export const FULL_ADMIN_ROLES = ['owner', 'ld_leader', 'principal'];

export const ALL_ROLES = Object.keys(ROLE_LABELS);

// Per-role section access. 'edit' means view + edit. Absent key = 'none' (tab hidden).
// Matrix derived from the Clarity4Growth improvements doc (role × section
// grid). Leadership-only ancillary buttons (activity log, NCCD readiness)
// are gated separately via CONFIDENTIAL_ROLES below.
export const ROLE_SECTIONS = {
  // Every non-confidential tab is open to every teacher-class role. Edit
  // levels stay role-appropriate (e.g. diagnosis stays view-only for
  // non-leadership — locked by canEditDiagnosis on top of this). The
  // Confidential tab is gated separately via canSeeConfidential().
  //
  // `mtss` is the new MTSS Adjustment Triangle (Assessing Need →
  // Diagnosis Support Triangle). `specialist` is the standalone
  // Specialist Adjustments tab also under Assessing Need.
  // `profile` and `adjustments` are kept for legacy access checks
  // (PDF builders, AI bundle) but the tabs themselves redirect to mtss.
  owner:        { summary:'edit', notes:'edit', behaviourMonitor:'edit', mtss:'edit', specialist:'edit', fba:'edit', behaviourRating:'edit', profile:'edit', adjustments:'edit', goals:'edit', interventions:'edit', supportTriangle:'edit', meetings:'edit', plans:'edit', incidents:'edit', timetable:'edit', emails:'edit', informalChats:'edit' },
  // Learning Diversity Leader + Principal/Deputy — full access, same as owner.
  ld_leader:    { summary:'edit', notes:'edit', behaviourMonitor:'edit', mtss:'edit', specialist:'edit', fba:'edit', behaviourRating:'edit', profile:'edit', adjustments:'edit', goals:'edit', interventions:'edit', supportTriangle:'edit', meetings:'edit', plans:'edit', incidents:'edit', timetable:'edit', emails:'edit', informalChats:'edit' },
  principal:    { summary:'edit', notes:'edit', behaviourMonitor:'edit', mtss:'edit', specialist:'edit', fba:'edit', behaviourRating:'edit', profile:'edit', adjustments:'edit', goals:'edit', interventions:'edit', supportTriangle:'edit', meetings:'edit', plans:'edit', incidents:'edit', timetable:'edit', emails:'edit', informalChats:'edit' },
  assistant:    { summary:'edit', notes:'edit', behaviourMonitor:'edit', mtss:'edit', specialist:'edit', fba:'edit', behaviourRating:'edit', profile:'edit', adjustments:'edit', goals:'edit', interventions:'edit', supportTriangle:'edit', meetings:'edit', plans:'edit', incidents:'edit', timetable:'edit', emails:'edit', informalChats:'edit' },
  executive:    { summary:'edit', notes:'edit', behaviourMonitor:'edit', mtss:'edit', specialist:'edit', fba:'edit', behaviourRating:'edit', profile:'edit', adjustments:'edit', goals:'edit', interventions:'edit', supportTriangle:'edit', meetings:'edit', plans:'edit', incidents:'edit', timetable:'edit', emails:'edit', informalChats:'edit' },
  // Leadership (RESTRICTED per the role spec): summary view-only, staff notes
  // see+add, triangle/specialist/goals view, plans view+edit, meetings view,
  // emails send-only (no history — see EMAIL_SEND_ONLY_ROLES).
  leadership:   { summary:'view', notes:'edit', behaviourMonitor:'edit', mtss:'view', specialist:'view', fba:'view', behaviourRating:'view', profile:'view', adjustments:'view', goals:'view', interventions:'view', supportTriangle:'view', meetings:'view', plans:'edit', incidents:'view', timetable:'view', emails:'edit', informalChats:'view' },
  // Specialist teacher: summary view, staff notes edit, specialist adjustments
  // edit, plans view. Everything else view.
  specialist:   { summary:'view', notes:'edit', behaviourMonitor:'edit', mtss:'view', specialist:'edit', fba:'view', behaviourRating:'view', profile:'view', adjustments:'view', goals:'view', interventions:'view', supportTriangle:'view', meetings:'view', plans:'view', incidents:'view', timetable:'view', emails:'none', informalChats:'view' },
  // Intervention teacher: summary view, notes see+add, triangle view, goals
  // edit, interventions edit, timetables+meetings view, emails send-only.
  intervention: { summary:'view', notes:'edit', behaviourMonitor:'edit', mtss:'view', specialist:'view', fba:'view', behaviourRating:'view', profile:'view', adjustments:'view', goals:'edit', interventions:'edit', supportTriangle:'view', meetings:'view', plans:'view', incidents:'view', timetable:'view', emails:'edit', informalChats:'view' },
  // Classroom teacher: edits everything they can see (scoped to their own
  // classes via canViewStudent). Confidential gated separately.
  grade_teacher:{ summary:'edit', notes:'edit', behaviourMonitor:'edit', mtss:'edit', specialist:'edit', fba:'edit', behaviourRating:'edit', profile:'view', adjustments:'edit', goals:'edit', interventions:'view', supportTriangle:'edit', meetings:'edit', plans:'view', incidents:'edit', timetable:'edit', emails:'view', informalChats:'edit' },
  // General teacher: ONLY summary (view) + staff notes (add comments). Across
  // all students. Every other tab hidden.
  all_teachers: { summary:'view', notes:'edit' },
  // ESO: only their assigned kids. Summary view, staff notes see+add, goals
  // view, plans view.
  eso:          { summary:'view', notes:'edit', behaviourMonitor:'edit', goals:'view', plans:'view', mtss:'view', specialist:'view', profile:'view', adjustments:'view', supportTriangle:'view', meetings:'view', timetable:'view', informalChats:'view' },
  // CRT (Casual Relief Teacher): a fully SANDBOXED login. No access to any
  // student tab or page — they only ever see the dedicated /crt daily-report
  // page for the one class an admin assigned them for the day. Enforced client
  // (isCrt gate on boot + router) AND server (firestore.rules).
  crt:          {},
};

// Roles that can SEND emails but must NOT see past email history.
const EMAIL_SEND_ONLY_ROLES = ['leadership', 'intervention'];

// Per the role spec, the big cohort AI insights, bulk import, the admin area,
// settings, and appointment setup are the FULL-ADMIN tier ONLY (Owner / LD
// Leader / Principal). Executive + assistant are no longer in these lists.
const ALL_BUTTONS_ROLES = [...FULL_ADMIN_ROLES];

// Roles that can add new students (admin area is full-admin only).
const ADD_STUDENT_ROLES = [...FULL_ADMIN_ROLES];

// Confidential: activity log + NCCD readiness visible only to the full-admin tier.
const CONFIDENTIAL_ROLES = [...FULL_ADMIN_ROLES];

// Roles that have full admin/settings access (including who can see Settings page)
const SETTINGS_ROLES = [...FULL_ADMIN_ROLES];

export const SECTION_KEYS = ['summary', 'profile', 'notes', 'adjustments', 'goals', 'meetings', 'plans', 'incidents', 'timetable', 'emails'];

// === STAFF CODES ===
// After Google sign-in, staff enter a one-time code that maps to their role.
// The code is stored on their /users/{uid} doc so they only enter it once.
// Format: prefix + number, e.g. "Care3", "Exec7", "Help0".
//
// Code prefix → role mapping:
//   Safe    → assistant       (Liz's assistant)
//   Exec    → executive       (Executive team)
//   Lead    → leadership      (Leadership)
//   Fun     → specialist      (Specialist teachers — Art, PE, Music)
//   Inter   → intervention    (Intervention teachers)
//   Care    → grade_teacher   (Classroom teachers — number = grade index)
//   Help    → eso             (Education Support Officers)

const STAFF_CODE_MAP = {
  safe:  { role: 'assistant',    label: "Liz's Assistant" },
  ldl:   { role: 'ld_leader',    label: 'Learning Diversity Leader' },
  prin:  { role: 'principal',    label: 'Principal / Deputy' },
  exec:  { role: 'executive',    label: 'Executive Team' },
  lead:  { role: 'leadership',   label: 'Leadership' },
  fun:   { role: 'specialist',   label: 'Specialist Teacher' },
  inter: { role: 'intervention', label: 'Intervention Teacher' },
  care:  { role: 'grade_teacher', label: 'Classroom Teacher' },
  help:  { role: 'eso',          label: 'ESO' },
};

// Grade mapping for Care codes: Care0=Prep, Care1=Year 1, ... Care6=Year 6.
// Care7-20 are spare slots for multiple teachers in the same grade.
const CARE_YEAR_LEVELS = ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];

/**
 * Parse and validate a staff code. Returns { valid, role, yearLevel, label }
 * or { valid: false, error: '...' }.
 */
export function parseStaffCode(code) {
  if (!code || typeof code !== 'string') return { valid: false, error: 'Enter a staff code' };
  const trimmed = code.trim();
  // Extract prefix (letters) and number
  const match = trimmed.match(/^([a-zA-Z]+)\s*[-]?\s*(\d+)$/);
  if (!match) return { valid: false, error: 'Invalid format - should be like Care3 or Exec0' };
  const prefix = match[1].toLowerCase();
  const num = parseInt(match[2], 10);
  const mapping = STAFF_CODE_MAP[prefix];
  if (!mapping) return { valid: false, error: `Unknown code prefix "${match[1]}". Valid: Safe, Exec, Lead, Fun, Inter, Care, Help` };

  // Validate number ranges
  const ranges = { safe: [0, 2], ldl: [0, 4], prin: [0, 4], exec: [0, 9], lead: [0, 9], fun: [10, 19], inter: [0, 9], care: [0, 20], help: [0, 9] };
  const [min, max] = ranges[prefix] || [0, 99];
  if (num < min || num > max) return { valid: false, error: `${match[1]} codes range from ${min} to ${max}` };

  // Year level for classroom teachers
  let yearLevel = null;
  if (prefix === 'care' && num <= 6) {
    yearLevel = CARE_YEAR_LEVELS[num];
  } else if (prefix === 'care') {
    // Care7-20 — spare slots, no specific year assigned (admin sets later)
    yearLevel = null;
  }

  return { valid: true, role: mapping.role, yearLevel, label: mapping.label, code: trimmed };
}

/** Normalise legacy roles to the new 9-role model */
function normaliseRole(role) {
  if (role === 'admin') return 'executive';
  // Legacy 'user' role had broad access (view most, edit notes + meetings).
  // Map to 'grade_teacher' which mirrors that — NOT 'all_teachers' which is
  // the minimal read-only tier and would hide meetings/adjustments/etc.
  if (role === 'user') return 'grade_teacher';
  if (ROLE_SECTIONS[role] || role === 'custom') return role;
  // Roles that live in the ROLE_MATRIX but have no legacy ROLE_SECTIONS entry
  // (eso_intervention, general_leadership, ld_assistant) must keep their own
  // identity. They used to fall through to the grade_teacher default below,
  // which silently PROMOTED them — St Brigid's five ESO-Intervention staff
  // were running the whole UI as classroom teachers (meetings, emails, plans
  // and diagnosis all editable) while the Firestore rules still enforced the
  // narrower eso_intervention matrix, so every one of those controls would
  // have failed on save.
  if (ROLE_MATRIX[role]) return role;
  return 'grade_teacher'; // safe default for a genuinely unknown role
}

export function hasPermission(action) {
  const role = currentUserRole;
  if (FULL_ADMIN_ROLES.includes(role)) return true;
  // Executive edits all student data but is NOT full-admin: settings, config and
  // user management are the full-admin tier only. The server denies executive on
  // /settings, /config and /users writes (isLeadership excludes executive), so
  // blocking these client-side stops dead controls that silently fail on save.
  if (role === 'executive') {
    const execBlocked = ['edit_settings', 'edit_config', 'manage_users'];
    return !execBlocked.includes(action);
  }
  if (role === 'assistant') {
    const assistantBlocked = ['fill_leadership_followup', 'edit_settings', 'manage_users'];
    return !assistantBlocked.includes(action);
  }
  if (role === 'custom') {
    return currentUserPermissions?.[action] === true;
  }
  // Leadership can do most things except settings/config/user management
  if (role === 'leadership') {
    const leadershipBlocked = ['edit_settings', 'edit_config', 'manage_users'];
    return !leadershipBlocked.includes(action);
  }
  // Roles that can edit student data (shown the Admin link for adding/editing students)
  if (['intervention', 'grade_teacher'].includes(role)) {
    if (action === 'edit_students' || action === 'view_students' || action === 'view_reports' || action === 'add_notes') return true;
    return false;
  }
  // All other roles: limited set of actions
  const viewActions = ['view_students', 'view_reports'];
  if (viewActions.includes(action)) return true;
  // Matrix roles carry their notes grant in ROLE_MATRIX, not the legacy
  // ROLE_SECTIONS table — check both, or a matrix-only role (eso_intervention)
  // silently loses the ability to add a staff note.
  if (action === 'add_notes' && sectionAccess('notes') === 'edit') return true;
  return false;
}

export function isLeadership() {
  return [...FULL_ADMIN_ROLES, 'executive'].includes(currentUserRole);
}
export function isLeadershipOrAdmin() {
  return [...FULL_ADMIN_ROLES, 'assistant', 'executive'].includes(currentUserRole);
}

/**
 * The per-student Activity Log (who viewed/edited what) is a sensitive audit
 * surface — restricted to top-level leadership ONLY (owner / LD leader /
 * principal, plus the legacy "leadership" alias). Deliberately excludes
 * executive and assistant, who can edit student data but must not see the
 * staff-level access trail.
 */
export function canViewActivityLog() {
  // A custom user sees it only when explicitly granted in Settings → People.
  if (currentUserRole === 'custom') return currentUserPermissions?.viewActivityLog === true;
  return [...FULL_ADMIN_ROLES, 'leadership'].includes(currentUserRole);
}

/** Casual Relief Teacher — a sandboxed login that can ONLY use the /crt daily
 *  report page for their assigned class. Everything else is denied. */
export function isCrt() {
  return currentUserRole === 'crt';
}

/** Can the current user see (read) this student at all?
 *
 * Resolution order:
 *   1. Leadership / owner / executive / assistant — always see everyone.
 *   2. Per-user student ID allowlist (set by admin) — sees only those IDs.
 *   3. Per-user year-level allowlist (set by admin) — sees that cohort.
 *   4. Legacy single yearLevel (grade_teacher role).
 *   5. Custom role permissions (legacy customPermissions.studentIds / yearLevels).
 *   6. Otherwise: see everyone.
 *
 * If the user has BOTH year levels and student IDs set, both apply
 * (union of "student in my year level" + "student explicitly granted").
 */
/** Resolve the current login to a /staff id by matching email. Used when a
 *  user doc has no explicit linkedStaffId — teacher emails are required so a
 *  teacher login lands on their roster entry automatically. */
function resolveLinkedStaffIdByEmail() {
  const email = (currentUser?.email || '').trim().toLowerCase();
  if (!email) return null;
  const match = (window.nccd?.staff || []).find(s => (s.email || '').trim().toLowerCase() === email);
  return match ? match.id : null;
}

export function canViewStudent(student) {
  if (!student) return false;
  if (isLeadershipOrAdmin()) return true;

  const role = currentUserRole;
  // Matrix roles: every student appears (their non-yellow zones — Summary,
  // Staff Notes — are school-wide). Per-zone "own-level" gating restricts the
  // clinical tabs, not which students are visible. Enforced per-zone via
  // zoneAccessForStudent (client) + firestore.rules (server).
  if (roleUsesMatrix(role)) return true;

  // Only classroom teachers and ESOs are VIEW-scoped to their own students.
  // Every other role (leadership, specialist, intervention, general teacher)
  // sees the whole school — the new role spec restricts what they can EDIT,
  // not which students appear. Without this, a leadership/specialist account
  // whose email happens to match a /staff teacher entry was wrongly narrowed
  // to that one class's year level.
  // Custom is intentionally NOT view-scoped: like matrix roles it SEES every
  // student (summary + staff notes are school-wide); its other zones are
  // scoped per-student in zoneAccessForStudent. Only grade_teacher/eso are
  // view-scoped to their own students.
  if (role !== 'grade_teacher' && role !== 'eso') return true;
  const activeYear = window.nccd?.activeYear;
  const enr = activeYear
    ? (window.nccd?.enrolments || []).find(e => e.studentId === student.id && e.academicYear === activeYear)
    : null;

  // Linked-staff scope (teacher/ESO login bound to a /staff entry). Computed
  // live off the class roster / ESO assignments so changes take effect
  // in-session even before scopeStudentIds is re-expanded server-side. A
  // teacher sees the students in their class(es); an ESO sees the students
  // they're assigned to. This is the authoritative path for a linked login.
  //
  // If no explicit link is set, auto-match the signed-in user's email to a
  // /staff entry's email (teacher emails are required for exactly this).
  const linkedStaffId = currentUserLinkedStaffId || resolveLinkedStaffIdByEmail();
  if (linkedStaffId) {
    const staff = (window.nccd?.staff || []).find(s => s.id === linkedStaffId);
    if (staff?.kind === 'eso') {
      return Array.isArray(student.esoStaffIds) && student.esoStaffIds.includes(linkedStaffId);
    }
    if (staff?.kind === 'teacher') {
      if (!enr?.classId) return false;
      const cls = (window.nccd?.classes || []).find(c => c.id === enr.classId);
      return !!cls && cls.teacherId === linkedStaffId;
    }
    // Linked to a staff id that no longer exists — fall through to other rules.
  }

  // Per-user explicit scope (the new flow set up in Settings → People).
  const ids = currentUserScopeStudentIds;
  const yls = currentUserScopeYearLevels;
  if ((ids && ids.length) || (yls && yls.length)) {
    if (ids?.includes(student.id)) return true;
    if (yls?.length && enr && yls.includes(enr.yearLevel)) return true;
    return false;
  }

  // Legacy grade_teacher with single yearLevel field.
  if (role === 'grade_teacher') {
    if (!currentUserYearLevel) {
      console.warn('grade_teacher has no yearLevel assigned - showing all students. Set a year level in Settings → People.');
      return true;
    }
    return enr ? enr.yearLevel === currentUserYearLevel : false;
  }

  // Legacy custom role using customPermissions.
  if (role === 'custom') {
    const explicit = currentUserPermissions?.studentIds || [];
    if (explicit.includes(student.id)) return true;
    const customYls = currentUserPermissions?.yearLevels || [];
    if (customYls.length && enr && customYls.includes(enr.yearLevel)) return true;
    return false;
  }

  // Default: see everyone.
  return true;
}

/** Returns the subset of `students` the current user is allowed to see.
 *  USE THIS anywhere you render a student list, dropdown, picker, or count. */
export function getVisibleStudents(students) {
  if (!Array.isArray(students)) return [];
  if (isLeadershipOrAdmin()) return students;
  return students.filter(s => canViewStudent(s));
}

/** The current user's explicit per-student scope (empty array = no
 *  restriction). Mirrors users/{uid}.scopeStudentIds. */
export function getScopeStudentIds() {
  return Array.isArray(currentUserScopeStudentIds) ? currentUserScopeStudentIds : [];
}

/** True when the user may read the WHOLE students collection (a `list`).
 *  Mirrors the `students` list rule in firestore.rules exactly: leadership /
 *  admin, the assistant, and anyone with an empty scopeStudentIds see
 *  everyone; a user with an explicit scope must fetch their students by id
 *  (those single-doc reads are gated by canViewStudentId). Keeping this in
 *  lock-step with the rule prevents a scoped user's bulk getDocs from being
 *  rejected. */
export function canSeeAllStudents() {
  // The Summary (Key Facts) card + Staff Notes are school-wide for EVERY staff
  // role, so every user loads and lists the whole roster — every child appears
  // in the records sheet. Per-student clinical scoping is applied at the ZONE
  // level (zoneAccessForStudent hides the "View" links + firestore.rules gate
  // the subcollections), NOT by hiding whole students. The sandboxed CRT never
  // reaches this (isCrt gate on boot). Mirrors the `/students list` rule.
  return currentUserRole !== 'crt';
}

/** True if the current user has any scope restriction applied. Use to
 *  decide whether to hide whole-school views, restrict pickers etc. */
export function hasRestrictedScope() {
  if (isLeadershipOrAdmin()) return false;
  if (currentUserLinkedStaffId || resolveLinkedStaffIdByEmail()) return true;
  if ((currentUserScopeStudentIds || []).length) return true;
  if ((currentUserScopeYearLevels || []).length) return true;
  if (currentUserRole === 'grade_teacher' && currentUserYearLevel) return true;
  return false;
}

/** What level of access does the current user have to this section?
 *  Returns 'edit' | 'view' | 'none'. */
/** True when this role is defined by the new ROLE_MATRIX (per-zone access +
 *  own-year-level scoping). owner is treated as full-admin. */
function roleUsesMatrix(role) {
  return role === 'owner' || !!ROLE_MATRIX[role];
}

/** Base access level a role has for a zone, ignoring per-student scoping.
 *  owner → edit everything; matrix roles → the matrix; custom → customPermissions;
 *  legacy roles (leadership/assistant/all_teachers/…) → the legacy ROLE_SECTIONS. */
export function sectionAccess(sectionKey) {
  const role = currentUserRole;
  if (role === 'owner') return 'edit';
  if (ROLE_MATRIX[role]) return roleZoneAccess(role, sectionKey);
  if (role === 'custom') {
    // Read through the legacy-key aliases: a custom user saved before the
    // editor was fixed still stores Group Monitoring under `cap_monitoring`
    // and Assessment Data under `cap_assessments`. Without this, re-saving a
    // user in Settings silently revokes both.
    return resolveZoneValue(currentUserPermissions?.sections, sectionKey);
  }
  return ROLE_SECTIONS[role]?.[sectionKey] || 'none';
}

/** Is a student in the current user's own year level(s)? Used to gate the
 *  "yellow" (own-level) zones. Reads the student's enrolment for the active
 *  year against the user's scopeYearLevels. Empty scope → treat as all levels. */
export function isStudentOwnLevel(studentId) {
  const yls = currentUserScopeYearLevels || [];
  if (!yls.length) return true; // no own-level restriction configured
  const activeYear = window.nccd?.activeYear;
  const enr = (window.nccd?.enrolments || []).find(e => e.studentId === studentId && e.academicYear === activeYear);
  return !!enr && yls.includes(enr.yearLevel);
}

/**
 * Access level for a specific zone on a specific student, honouring own-level
 * scoping. If the zone is "yellow" for this role and the student is not in the
 * user's own year level(s), access is 'none' (invisible for that student).
 */
export function zoneAccessForStudent(sectionKey, studentId) {
  const base = sectionAccess(sectionKey);
  if (base === 'none') return 'none';
  const role = currentUserRole;
  if (roleUsesMatrix(role) && role !== 'owner' && roleZoneScoped(role, sectionKey) && !isStudentOwnLevel(studentId)) {
    return 'none';
  }
  // Custom role: Summary + Staff Notes are school-wide (every student); every
  // OTHER granted zone is scoped to the user's scopeStudentIds / scopeYearLevels.
  if (role === 'custom' && sectionKey !== 'summary' && sectionKey !== 'notes') {
    const ids = getScopeStudentIds();
    const yls = currentUserScopeYearLevels || [];
    if (ids.length || yls.length) {
      const inScope = ids.includes(studentId) || (yls.length && isStudentOwnLevel(studentId));
      if (!inScope) return 'none';
    }
  }
  return base;
}

/** True if a capability zone (cap_*) is granted to the current role. */
export function capabilityAccess(capKey) {
  return sectionAccess(capKey);
}

/** Can the current user sign on behalf of OTHER staff - i.e. pick any colleague's
 *  saved signature from the library on a meeting, plan, incident or FBA?
 *  Everyone can always use their OWN signature; this is the extra grant that
 *  loads the whole staff signature feed (Owner / LD Leader / Executive by
 *  default, and a Custom user can be given it explicitly). */
export function canUseSignatureLibrary() {
  if (roleUsesMatrix(currentUserRole) || currentUserRole === 'custom') {
    return capabilityAccess('cap_signatures') !== 'none';
  }
  return isLeadership();
}

/** Can the current user fill in leadership follow-up notes? Confidential roles only. */
export function canFillLeadershipFollowup() {
  if (CONFIDENTIAL_ROLES.includes(currentUserRole)) return true;
  if (currentUserRole === 'custom') return currentUserPermissions?.canFillLeadershipFollowup === true;
  return false;
}

/** Can the current user see AI buttons, readiness scan, import, etc.? */
export function canSeeAllButtons() {
  if (roleUsesMatrix(currentUserRole)) return capabilityAccess('cap_ai') !== 'none';
  return ALL_BUTTONS_ROLES.includes(currentUserRole);
}

// === Bulk Editor per-stage access (see data/bulk-editor-access.js) ===========
// The Bulk Editor (guided-setup) has nine numbered stages. Access is per-stage:
// the leadership tier gets all of them unscoped; specific teacher/intervention/
// specialist roles get particular stages, SCOPED to their own students.

/** Can the current user open a specific Bulk Editor stage (by key)? */
export function canUseBulkStage(key) {
  const s = bulkStageDef(key);
  if (!s) return false;
  const role = currentUserRole;
  if (role === 'owner') return true;
  if (role === 'custom') return currentUserPermissions?.bulkStages?.[key] === true;
  return s.full.includes(role) || s.scoped.includes(role);
}

/** For a stage the user CAN open, is their access limited to their own
 *  students (class / group / year level / caseload)? Leadership → false. */
export function bulkStageScoped(key) {
  const s = bulkStageDef(key);
  if (!s) return false;
  const role = currentUserRole;
  if (role === 'owner') return false;
  if (role === 'custom') {
    // Custom roles are scoped unless they carry an explicit whole-school flag.
    return currentUserPermissions?.bulkStages?.[key] === true
      && currentUserPermissions?.bulkStagesFull?.[key] !== true;
  }
  return s.scoped.includes(role) && !s.full.includes(role);
}

/** Does the user have at least one Bulk Editor stage? Gates the entry button. */
export function canUseAnyBulkStage() {
  return BULK_STAGES.some(s => canUseBulkStage(s.key));
}

/** Ids of the classes the current user is the CLASS TEACHER of (via their
 *  linked staff record). Empty for non-teachers / unlinked logins. */
export function getOwnClassIds() {
  const staffId = currentUserLinkedStaffId || resolveLinkedStaffIdByEmail();
  if (!staffId) return [];
  return (window.nccd?.classes || []).filter(c => c.teacherId === staffId).map(c => c.id);
}

/** Does the current user teach a given year level? True if their year-level
 *  scope includes it, or they're the class teacher of a class covering it.
 *  Used to open the Pre-Prep intake view to current Prep teachers. */
export function teachesYearLevel(yl) {
  if (!yl) return false;
  const target = String(yl).toLowerCase();
  const yls = currentUserScopeYearLevels || [];
  if (yls.some(y => String(y).toLowerCase() === target)) return true;
  const staffId = currentUserLinkedStaffId || resolveLinkedStaffIdByEmail();
  if (staffId) {
    const classes = window.nccd?.classes || [];
    return classes.some(c => c.teacherId === staffId
      && (c.yearLevels || []).some(y => String(y).toLowerCase() === target));
  }
  return false;
}

/** Can the current user run PER-STUDENT AI (readiness, SMART goal, executive
 *  summary, document summary) on students they can see? The full-admin tier
 *  plus classroom teachers (for their own kids). NOT level recalibration —
 *  that's canRecalibrateLevel(). */
export function canUseStudentAi(studentId) {
  // Goals-editors (intervention team, grade teachers) keep the per-student
  // helpers (SMART goal, polish) even when the AI Insights page (cap_ai) is
  // off for their role — writing NCCD-ready goals is their core job.
  //
  // `goals` is an own-level ("yellow") zone for those roles, so it must be
  // evaluated FOR THIS STUDENT. Without the studentId the unscoped grant won
  // and an intervention teacher got Generate-Executive-Summary buttons on
  // children outside their year levels — students whose diagnosis and goals
  // they cannot even read.
  if (roleUsesMatrix(currentUserRole)) {
    if (capabilityAccess('cap_ai') !== 'none') return true;
    return (studentId ? zoneAccessForStudent('goals', studentId) : sectionAccess('goals')) === 'edit';
  }
  return canSeeAllButtons() || currentUserRole === 'grade_teacher';
}

// Granular AI sub-actions a CUSTOM user can be granted individually (mirrors the
// AI section in the custom-permissions editor). Allowed if they hold the
// top-level "all" AI grant OR that specific sub-flag.
function customAiGranted(sub) {
  const ai = currentUserPermissions?.ai;
  return !!ai && (ai.all === true || ai[sub] === true);
}
/** Per-FEATURE AI gate. For CUSTOM users it honours the granular AI toggles
 *  (docSummary / studentSummary / writingHelp / readinessScan / cohort); for
 *  every other role it defers to the existing canUseStudentAi() behaviour so
 *  preset roles are completely unchanged. This is what individual AI buttons
 *  (e.g. "Summarise document → meeting") should check. */
export function aiFeatureAllowed(sub, studentId) {
  if (currentUserRole === 'custom') return customAiGranted(sub);
  return canUseStudentAi(studentId);
}
/** Cohort AI Insights page (submission review, year handover, etc.).
 *  Matrix roles follow their cap_ai grant — the same switch the Roles matrix
 *  editor controls — so granting a role AI Insights actually opens the page,
 *  not just the per-student buttons. The server allows the same set
 *  (functions/src/api/ai.ts AI_ROLES). */
export function canUseCohortAi() {
  if (currentUserRole === 'custom') return customAiGranted('cohort');
  if (roleUsesMatrix(currentUserRole)) return capabilityAccess('cap_ai') !== 'none';
  return canSeeAllButtons();
}

/** Can the current user add new students? */
export function canAddStudents() {
  if (roleUsesMatrix(currentUserRole)) return capabilityAccess('cap_admin') === 'edit';
  if (currentUserRole === 'custom') return sectionAccess('cap_admin') === 'edit';
  return ADD_STUDENT_ROLES.includes(currentUserRole);
}

/** Can the current user edit diagnosis / NCCD level fields?
 *  Doc rule: locked unless full-admin tier or Liz's assistant/executive. */
/** May this user change (and lock) the Learning Diversity PSG ↔ Extended
 *  cadence on a meeting? Leadership tier always; a custom user only when
 *  explicitly granted "Set PSG / Extended" in Settings → Users. It used to be
 *  unlocked for EVERY custom user — broader than intended; now it's a grant,
 *  like editDiagnosis. */
export function canSetMeetingSubtype() {
  if (isLeadershipOrAdmin()) return true;
  return currentUserRole === 'custom' && currentUserPermissions?.setMeetingSubtype === true;
}

export function canEditDiagnosis() {
  if (roleUsesMatrix(currentUserRole)) return sectionAccess('mtss') === 'edit';
  if ([...FULL_ADMIN_ROLES, 'assistant', 'executive'].includes(currentUserRole)) return true;
  // Custom users only get it if explicitly granted (defaults off = owner-only).
  return currentUserRole === 'custom' && currentUserPermissions?.editDiagnosis === true;
}

/** Can the current user RECALIBRATE an NCCD adjustment level (the AI level
 *  re-check)? Full-admin tier only — classroom teachers explicitly cannot. */
export function canRecalibrateLevel() {
  return FULL_ADMIN_ROLES.includes(currentUserRole)
    || currentUserRole === 'executive'
    || currentUserRole === 'assistant';
}

/** Can the current user view PAST email history (not just send)? Send-only
 *  roles (leadership, intervention) can compose+send but never see history. */
export function canViewPastEmails() {
  if (EMAIL_SEND_ONLY_ROLES.includes(currentUserRole)) return false;
  if (currentUserRole === 'custom') return currentUserPermissions?.viewEmailHistory !== false;
  return true;
}

/** Can the current user use the postcode (address) search? The lookup lives
 *  inside MEETING notes (the LD "Local Services" add-on) as well as plans, so
 *  anyone who can edit either may run it. Originally plans-edit only, which
 *  locked out the intervention team (plans: view) from a field sitting in
 *  meetings they fully edit (Amanda + Melynda, 24 Aug 2026). */
export function canUsePostcodeSearch() {
  return sectionAccess('plans') === 'edit' || sectionAccess('meetings') === 'edit';
}

/** Can the current user SEE the Appointments page at all? 'view' or 'edit' on
 *  the cap_appointments zone. Custom roles read their per-user setting; legacy
 *  roles keep their historic (ungated) access. */
export function canViewAppointments() {
  const role = currentUserRole;
  if (roleUsesMatrix(role) || role === 'custom') return capabilityAccess('cap_appointments') !== 'none';
  if (role === 'all_teachers') return false; // no zones - rules deny it too
  return true; // legacy roles (leadership/assistant) - unchanged
}

/** Top-level nav features that a CUSTOM role can be granted/denied via the
 *  permission matrix (Referrals, Group Monitoring, Assessments, Library). Only
 *  the custom role is gated — every other role keeps its historic access, so
 *  turning these on for custom users never changes anyone else. */
function canViewNavCap(capKey) {
  // Matrix roles are governed by their matrix cell. This used to return `true`
  // for every non-custom role, so the matrix was ignored entirely here and a
  // Specialist Teacher got the Assessments page despite assessmentData=none.
  if (roleUsesMatrix(currentUserRole) || currentUserRole === 'custom') {
    return capabilityAccess(capKey) !== 'none';
  }
  // all_teachers is the DEFAULT role a self-registered staff member lands on
  // and carries NO zones — the Firestore rules give it summary + staff notes
  // and nothing else. The client used to show every one of these pages to it,
  // so a general teacher saw Group Monitoring / Assessments / Library in the
  // nav, opened them, and hit permission-denied. Mirror the server.
  if (currentUserRole === 'all_teachers') return false;
  return true;
}
/** The Referrals nav dropdown — the staff / parent / student referral FORMS.
 *
 *  Raising a concern about a child is a baseline duty-of-care action for ANY
 *  staff member, and the Firestore rules agree: `allow create` on /referrals
 *  is open, while `allow read` is leadership-or-cap_referrals. So the forms are
 *  open to everyone and `cap_referrals` governs the INBOX instead — see
 *  canViewReferralsInbox(). Gating the forms by the matrix would have taken the
 *  "raise a concern" path away from every ESO. */
export function canViewReferrals() { return true; }

/** The Referrals INBOX — reviewing referrals other people have raised, which
 *  can include a parent's or a child's own "I need some help" disclosure.
 *
 *  Leadership tier only. Confirmed with the school: `cap_referrals` in the
 *  roles matrix is labelled "Referral forms" and means "may RAISE a referral"
 *  — which everyone can now do (see canViewReferrals) — not "may read everyone
 *  else's". The Firestore rules used to be looser than this (any matrix role or
 *  custom user holding cap_referrals could read the collection, i.e. ~20 staff);
 *  they have been tightened to match this function exactly. The same gate
 *  governs the Referrals section of a student PDF, which is the same content. */
export function canViewReferralsInbox() {
  return isLeadershipOrAdmin();
}
export function canViewMonitoring() { return canViewNavCap('cap_groupMonitoring'); }
export function canViewAssessments() { return canViewNavCap('assessmentData'); }
export function canViewLibrary() { return canViewNavCap('cap_library'); }
/** Top-level Interventions page — anyone whose interventions zone isn't
 *  Invisible (matrix/custom); legacy roles keep historic access. */
/** Top-level Timetables page (`/eso`). Was completely ungated — every role,
 *  including all_teachers and custom users with no timetable grant at all,
 *  got the nav link. Allow anyone holding EITHER the timetable capability or
 *  the per-student timetable zone, so custom users configured with only one
 *  of the two keep their access. */
export function canViewTimetablesNav() {
  const role = currentUserRole;
  if (roleUsesMatrix(role) || role === 'custom') {
    return capabilityAccess('cap_timetables') !== 'none' || sectionAccess('timetable') !== 'none';
  }
  if (role === 'all_teachers') return false; // no zones - rules deny it too
  return true;
}
export function canViewInterventionsNav() {
  const role = currentUserRole;
  if (roleUsesMatrix(role) || role === 'custom') return sectionAccess('interventions') !== 'none';
  if (role === 'all_teachers') return false; // no zones - rules deny it too
  return true;
}
/** Can the current user ADD resources to the Library? Matrix/custom roles with
 *  cap_library edit (that's nearly all staff); legacy roles → leadership tier.
 *  Editing an EXISTING item is ownership-gated on top of this (see library.js):
 *  leadership/admin edit anything, everyone else only what they added. */
export function canEditLibrary() {
  if (roleUsesMatrix(currentUserRole) || currentUserRole === 'custom') return capabilityAccess('cap_library') === 'edit';
  return isLeadershipOrAdmin();
}

/** Can the current user SET UP / edit appointments? Requires 'edit' on the
 *  cap_appointments zone (matrix + custom roles); legacy roles fall back to the
 *  full-admin tier. Everyone with 'view' can see appointments but not change them. */
export function canManageAppointments() {
  const role = currentUserRole;
  if (roleUsesMatrix(role) || role === 'custom') return capabilityAccess('cap_appointments') === 'edit';
  return FULL_ADMIN_ROLES.includes(role);
}

/** Fire-and-forget log of an attempted unauthorised action. Writes to the
 *  central /auditLog collection with type=`unauthorised_access`, which the
 *  onUnauthorisedAccess Cloud Function picks up and emails to leadership
 *  + the owner. Silently no-ops if the user isn't signed in. */
export async function logUnauthorisedAccess(action, resource) {
  if (!currentUser) return;
  try {
    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
    await addDoc(collection(db, 'auditLog'), {
      type: 'unauthorised_access',
      userEmail: currentUser.email || '',
      userUid: currentUser.uid || '',
      role: currentUserRole,
      action: action || 'unspecified',
      resource: resource || '',
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Failed to record unauthorised access attempt', err);
  }
}

/** Can the current user see confidential data (activity log, NCCD readiness)? */
export function canSeeConfidential() {
  if (roleUsesMatrix(currentUserRole)) return sectionAccess('confidential') !== 'none';
  if (CONFIDENTIAL_ROLES.includes(currentUserRole)) return true;
  // Custom users only get it if explicitly granted (defaults off = owner-only).
  return currentUserRole === 'custom' && currentUserPermissions?.confidential === true;
}

/**
 * Sub-actions — finer-grained permissions INSIDE a zone the user already holds.
 * See data/zone-subactions.js for the model and, importantly, the limits of
 * what they enforce.
 *
 * DEFAULT ON: anyone holding the zone gets every sub-action unless an admin has
 * explicitly taken one away. Only Custom users can have one removed today -
 * a preset role's sub-actions all follow from its zone access - so a matrix
 * role always answers true here.
 */
export function canDoSubAction(zone, key, studentId) {
  // The zone itself is the real gate. No access to the area, no sub-action.
  const access = studentId ? zoneAccessForStudent(zone, studentId) : sectionAccess(zone);
  if (access === 'none') return false;
  // Staff notes are GLOBAL (school decision, 14 Aug 2026) and so is notifying
  // about them: 'Add & Notify' is for every staff member, custom users
  // included — no per-user sub-action tick required. An explicit notes:none
  // opt-out still removes it via the access gate above.
  if (zone === 'notes' && key === 'addNotify') return true;
  if (currentUserRole !== 'custom') return true;
  return subActionAllowed(currentUserPermissions?.subActions, zone, key);
}

/** Can the current user EMAIL the Home Situation sheet?
 *
 *  Exec tier only, and separate from merely being able to READ the Confidential
 *  tab. Reading it is a supervised act inside the app with an access-log entry
 *  against the child; emailing it puts protected home-situation information -
 *  DHHS involvement, court orders, family violence - into an inbox where the
 *  app can no longer control who sees it. A Custom user needs the grant
 *  explicitly; ticking "confidential" alone is not enough. */
const HOME_SITUATION_EMAIL_ROLES = ['owner', 'principal', 'ld_leader', 'executive', 'leadership'];
export function canEmailHomeSituation() {
  if (!canSeeConfidential()) return false;
  if (currentUserRole === 'custom') {
    // Granted explicitly, then still subject to the sub-action tick - so it can
    // be given and later withdrawn without touching the Confidential grant.
    return currentUserPermissions?.emailHomeSituation === true
      && subActionAllowed(currentUserPermissions?.subActions, 'confidential', 'emailHomeSituation');
  }
  return HOME_SITUATION_EMAIL_ROLES.includes(currentUserRole);
}

/** Can the current user access settings/config/user management? */
/** AI NCCD readiness scans (bulk + per-student) — Owner, LD Leader,
 *  Principal/Deputy and Exec only. Custom users need an explicit grant. */
export function canRunReadinessScan() {
  if (currentUserRole === 'custom') {
    // Legacy readinessScan flag OR the new AI section's readiness/all grant.
    return currentUserPermissions?.readinessScan === true || customAiGranted('readinessScan');
  }
  return FULL_ADMIN_ROLES.includes(currentUserRole) || currentUserRole === 'executive';
}

export function canAccessSettings() {
  if (roleUsesMatrix(currentUserRole)) return capabilityAccess('cap_settings') !== 'none';
  if (currentUserRole === 'custom') return sectionAccess('cap_settings') !== 'none';
  return SETTINGS_ROLES.includes(currentUserRole);
}

// Email/password sign-in for the Playwright / Aikido security scan
// harness — gated behind `?audit=1` in the URL so production users
// never see this path. The audit account is NOT a master user and gets
// no automatic owner role; it is subject to the normal allowlist like any
// other account. (Email/Password must also be enabled on the Firebase
// project for this to work — it stays disabled in production.)
export async function loginWithEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

// Auto-sign-in helper invoked from main.js on boot when ?audit=1&audit_email=…
// &audit_password=… are present in the URL. Lets the audit script start a
// fresh session without any user interaction.
export async function tryAuditAutoLogin() {
  if (typeof location === 'undefined') return false;
  const params = new URLSearchParams(location.search);
  if (params.get('audit') !== '1') return false;
  const email = params.get('audit_email');
  const password = params.get('audit_password');
  if (!email || !password) return false;
  try {
    await signInWithEmailAndPassword(auth, email, password);
    return true;
  } catch (e) {
    console.error('Audit auto-login failed:', e?.message || e);
    return false;
  }
}

// Shared popup-driven sign-in. Always prefer signInWithPopup over
// signInWithRedirect — popup uses postMessage between the OAuth handler
// and the parent window, which works inside iOS Safari, Gmail / IG / FB
// in-app browsers, etc. Redirect fails in those contexts because of
// cross-origin sessionStorage partitioning ("Unable to process request
// due to missing initial state").
/**
 * Returns TRUE only when a user is actually signed in.
 *
 * This return value is load-bearing. Every branch below handles its error and
 * returns normally, so the caller cannot tell success from failure by whether
 * this throws. main.js used to assume "login() resolved = auth succeeded" and
 * render the "Signing you in..." spinner; when someone simply CANCELLED the
 * Google popup, that spinner stayed up forever waiting for an onAuthChange that
 * was never coming, and the boot watchdog then blamed the school network:
 * "Loading students timed out - your connection or the school network may be
 * blocking the database." Reported as a network/token outage; actually a
 * dismissed popup. Never return true from a path that did not sign anyone in.
 */
async function popupSignIn(providerInstance, providerLabel) {
  try {
    await signInWithPopup(auth, providerInstance);
    return true;
  } catch (err) {
    if (!err) return false;
    // User closed / cancelled the popup. Not an error worth shouting about,
    // but emphatically NOT a successful sign-in.
    if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return false;
    // web-storage-unsupported / "missing initial state" is what teachers hit
    // when they open the link from inside Gmail / Instagram / Facebook on iOS.
    // Previously only the getRedirectResult handler explained this; that call is
    // gone (see the note above onAuthStateChanged), so it belongs here.
    const isStorageState = err.code === 'auth/web-storage-unsupported'
      || /missing initial state|sessionStorage/i.test(err.message || '');
    if (isStorageState) {
      alert(
        'Sign-in didn\'t complete because this page opened inside an in-app browser (e.g. Gmail, Instagram, or Facebook).\n\n' +
        'Please open this link in your phone\'s main browser (Safari on iPhone, Chrome on Android) and try again.'
      );
      return false;
    }
    if (err.code === 'auth/popup-blocked' || err.code === 'auth/operation-not-supported-in-this-environment') {
      alert(
        'Sign-in popup was blocked by your browser. Please:\n\n' +
        '1. Allow popups for this site in your browser settings, or\n' +
        '2. Open this page in your phone\'s main browser (Safari / Chrome) instead of from inside Gmail / Instagram / Facebook,\n\n' +
        'and try again.'
      );
      return false;
    }
    if (err.code === 'auth/account-exists-with-different-credential') {
      // Same email registered with a different provider — tell the user
      // which provider they previously used so they don't get stuck.
      const otherProvider = err.customData?.email
        ? `with the other sign-in provider (you may have signed in via ${providerLabel === 'Microsoft' ? 'Google' : 'Microsoft'} previously using ${err.customData.email})`
        : 'with the other sign-in provider';
      alert(`This email is already registered ${otherProvider}. Please sign in that way instead, or contact your administrator to merge the accounts.`);
      return false;
    }
    console.error(`${providerLabel} login error:`, err);
    alert(friendlyError(err, 'Sign-in failed. Please try again.'));
    return false;
  }
}

export async function login() {
  return popupSignIn(provider, 'Google');
}

// Microsoft sign-in (Outlook / Microsoft 365 / Office 365 accounts).
// Uses Firebase Auth's OAuthProvider for `microsoft.com`. Requires the
// Microsoft provider to be enabled in Firebase Console → Authentication
// → Sign-in method (per project), with an Azure AD app registration
// configured. Same allowlist + role flow as the Google path —
// `onAuthStateChanged` doesn't care which provider was used.
export async function loginMicrosoft() {
  const msProvider = new OAuthProvider('microsoft.com');
  // `organizations` = work/school tenants ONLY — blocks personal
  // Microsoft accounts (outlook.com / hotmail.com / live.com / Xbox).
  // Required because NCCD data is PROTECTED-class and only authorised
  // school staff should sign in. The Azure AD app registration must
  // also be set to "Accounts in any organisational directory
  // (Multitenant)" — NOT the "and personal accounts" option — or this
  // client-side restriction can be bypassed.
  msProvider.setCustomParameters({ tenant: 'organizations', prompt: 'select_account' });
  msProvider.addScope('email');
  msProvider.addScope('profile');
  return popupSignIn(msProvider, 'Microsoft');
}

// Login-hang diagnostics: record how far the auth handshake got, so when the
// boot watchdog in main.js fires it can report EXACTLY where it stalled
// (never-fired = Firebase Auth state didn't propagate; reading-user-doc =
// Firestore read blocked; etc.). fullErrorReport() reads window.__authStage.
// A single mutable "current stage" LIES when two async chains are in flight.
// Real case (St Brigid's, Aug 2026): a report showed authStage
// 'redirect-result-done' while `user` was already populated — impossible in
// sequence, because currentUser is only set inside onAuthStateChanged, which
// stamps a later stage. What actually happened is the module-init
// getRedirectResult() promise resolved LATE and overwrote a stage the auth
// callback had already moved past, so we were reading the wrong stall point.
//
// Keep the last stage (existing consumers read it) but also record an ordered,
// timestamped trail. The trail is what actually tells us where the seconds
// went — and a slow getRedirectResult is itself a finding, because it waits on
// the cross-origin auth iframe rather than on Firestore.
const _stageTrail = [];
const _now = () => (typeof window !== 'undefined' && window.performance?.now) ? window.performance.now() : 0;
const _bootT0 = _now();
function _authStage(s) {
  if (typeof window === 'undefined') return;
  const at = _now();
  window.__authStage = s;
  _stageTrail.push(`+${Math.round(at - _bootT0)}ms ${s}`);
  window.__authTrail = _stageTrail;
}
_authStage('module-init');

// The FIRST Firestore read of the session, and the one that strands teachers on
// the boot spinner (confirmed in the wild: authStage 'reading-user-doc', St
// Brigid's, Aug 2026). Auth has already succeeded at this point — what's slow is
// the initial long-polled Firestore round-trip on a proxied school/home network,
// which can take far longer than a warm read. An unbounded await here means a
// merely SLOW connection looks identical to a dead one.
//
// So: time-box it, and retry once. The retry opens a fresh request and usually
// succeeds where the first stalled. Only if BOTH time out do we treat Firestore
// as genuinely unreachable.
async function readUserDocWithRetry(ref) {
  try {
    return await withTimeout(getDoc(ref), 8000, 'user-doc-read-timeout');
  } catch (e) {
    console.warn('First user-doc read stalled - retrying once:', e?.message || e);
    _authStage('reading-user-doc-retry');
    try {
      return await withTimeout(getDoc(ref), 10000, 'user-doc-read-timeout-2');
    } catch (e2) {
      // Both attempts stalled. On the default streaming transport this is the
      // blocked-proxy signature (confirmed at St Brigid's: managed Chrome that
      // black-holes WebChannel) — stamp this browser to forced long-polling
      // and reload; the next boot uses plain HTTPS and gets through. No-op if
      // the browser is ALREADY on long-polling (then it's a real outage) or
      // if a transport reload just happened (loop guard) — in those cases the
      // error surfaces to the normal boot-failure panel as before.
      _authStage('transport-fallback');
      if (fallBackToLongPolling('user-doc read stalled through both attempts')) {
        // Reload is in flight — park this chain so no error panel flashes.
        return new Promise(() => {});
      }
      throw e2;
    }
  }
}

// NO getRedirectResult() at module load — deliberate, and load-bearing.
//
// We sign in with signInWithPopup EXCLUSIVELY; signInWithRedirect is never
// called anywhere in src/. So getRedirectResult() could never return a result —
// but calling it made Firebase Auth resolve the redirect flow during init,
// which loads the hidden auth iframe from the cross-origin authDomain
// (nccd-<school>.firebaseapp.com) on EVERY page load, including for already
// signed-in users who never touch the sign-in button.
//
// That is a cross-origin round-trip on the critical boot path, and on a school
// network that throttles or inspects it, it is slow. A real report (cdunn,
// St Brigid's, Aug 2026) showed it still resolving after the boot watchdog had
// fired — it landed so late it overwrote the auth stage of a chain that had
// already moved on. Removing it takes the cross-origin iframe off the boot path
// entirely; the popup resolver still loads it, but lazily, at click time.
//
// onAuthStateChanged (below) restores a persisted session on its own — that has
// never depended on this call. In-app-browser storage errors are still caught
// and explained on the popup path, in popupSignIn().

export async function logout() {
  // Sign-out is an SPA state change, not a page unload — beforeunload never
  // fires, so without this guard it silently destroyed any unsaved work
  // (28 Aug 2026: staff "signed out to be safe" and lost hours of typing).
  try {
    const { isDirty, confirmIfDirty } = await import('./utils/dirty-tracker.js');
    if (isDirty() && !(await confirmIfDirty('sign out'))) return;
  } catch { /* the guard must never be able to block a sign-out entirely */ }
  await signOut(auth);
  currentUser = null;
  currentUserRole = 'all_teachers';
  currentUserPermissions = {};
  currentUserYearLevel = null;
  currentUserScopeYearLevels = [];
  currentUserScopeStudentIds = [];
}

// Listen for auth state changes
onAuthStateChanged(auth, async (user) => {
  currentUser = user;
  _authStage(user ? 'onAuthStateChanged:user' : 'onAuthStateChanged:null');
  if (user) {
    try {
    // Load or create user profile
    const userRef = doc(db, 'users', user.uid);
    _authStage('reading-user-doc');
    const userSnap = await readUserDocWithRetry(userRef);
    _authStage('user-doc-loaded');
    if (userSnap.exists()) {
      const data = userSnap.data();
      if (data.blocked) {
        // User exists but is not allowed
        await signOut(auth);
        currentUser = null;
        authListeners.forEach(cb => cb(null, 'user'));
        alert('Access denied. Contact your administrator.');
        return;
      }
      // Cache the user's preferred display name (saved override from
      // My Profile, falls back to Google's name on first sign-in).
      currentUserDisplayName = data.displayName || user.displayName || '';
      // Master users always get 'owner' — skip staff code prompt.
      const masterOverride = ['harry@easyclarity4growth.com', 'elizabeth@easyclarity4growth.com'].includes(user.email);
      if (masterOverride) {
        currentUserRole = 'owner';
        if (data.role !== 'owner') await setDoc(userRef, { role: 'owner', lastLogin: serverTimestamp() }, { merge: true });
        else await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
      } else if (data.staffCode) {
        // Returning user with a saved staff code — use stored role.
        currentUserRole = normaliseRole(data.role || 'all_teachers');
        currentUserYearLevel = data.yearLevel || null;
        await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
      } else {
        // Legacy or freshly-allowlisted record with no role assigned yet.
        // Default to all_teachers (read-mostly). Leadership can upgrade from
        // Settings, People at any time.
        currentUserRole = normaliseRole(data.role || 'all_teachers');
        currentUserYearLevel = data.yearLevel || null;
        await setDoc(userRef, {
          role: currentUserRole,
          lastLogin: serverTimestamp(),
        }, { merge: true });
      }
      currentUserPermissions = data.customPermissions || {};
      currentUserScopeYearLevels = Array.isArray(data.scopeYearLevels) ? data.scopeYearLevels : [];
      currentUserScopeStudentIds = Array.isArray(data.scopeStudentIds) ? data.scopeStudentIds : [];
      currentUserLinkedStaffId = data.linkedStaffId || null;
      currentUserPrefs = data.prefs || {};
    } else {
      // First login — three gates, any one passes:
      //   1. Hardcoded master users (Easy Clarity 4 Growth team)
      //   2. School's allowed-domain list (e.g. anyone at @stbrigids.vic.edu.au)
      //   3. Explicit emails on the allowlist
      const masterUsers = ['harry@easyclarity4growth.com', 'elizabeth@easyclarity4growth.com'];
      let isAllowed = masterUsers.includes(user.email);
      let schoolDomains = [];
      let allowedEmails = [];
      if (!isAllowed) {
        try {
          const [schoolSnap, allowlistSnap] = await Promise.all([
            getDoc(doc(db, 'settings', 'school')),
            getDoc(doc(db, 'settings', 'allowlist')),
          ]);
          schoolDomains = schoolSnap.exists() ? (schoolSnap.data().allowedDomains || []) : [];
          allowedEmails = allowlistSnap.exists() ? (allowlistSnap.data().emails || []) : [];
          const emailDomain = user.email.split('@')[1]?.toLowerCase();
          const inDomain = schoolDomains.length > 0
            && emailDomain
            && schoolDomains.map(d => d.toLowerCase()).includes(emailDomain);
          const inExplicit = allowedEmails.length > 0 && allowedEmails.includes(user.email);
          // SECURITY (audit HIGH #1): no open "no config → allow anyone" bootstrap.
          // A freshly provisioned school admits ONLY the vendor master (above)
          // until leadership sets the allowed domain / allowlist — otherwise any
          // Google/Microsoft account could self-register as the first owner.
          isAllowed = inDomain || inExplicit;
        } catch (e) {
          console.warn('Could not read school allowlist:', e);
        }
      }

      if (!isAllowed) {
        await signOut(auth);
        currentUser = null;
        authListeners.forEach(cb => cb(null, 'user'));
        const hint = schoolDomains.length
          ? `Only emails ending in ${schoolDomains.map(d => '@' + d).join(', ')} are allowed.`
          : 'Your email is not on the approved list.';
        alert(`Access denied. ${hint} Contact your school administrator.`);
        return;
      }

      // Check if master user (first login)
      const isMaster = ['harry@easyclarity4growth.com', 'elizabeth@easyclarity4growth.com'].includes(user.email);
      if (isMaster) {
        currentUserRole = 'owner';
        currentUserPermissions = {};
        currentUserYearLevel = null;
        await setDoc(userRef, {
          email: user.email, displayName: user.displayName,
          role: 'owner', staffCode: 'master', customPermissions: {},
          createdAt: serverTimestamp(), lastLogin: serverTimestamp(),
        });
      } else {
        // Check if a pre-created user doc exists (created by admin via email)
        const preCreatedId = user.email.replace(/[^a-zA-Z0-9]/g, '_');
        const preCreatedSnap = await getDoc(doc(db, 'users', preCreatedId));
        let preAssignedRole = null;
        let preAssignedCode = null;
        let preAssignedScopeYearLevels = [];
        let preAssignedScopeStudentIds = [];
        let preAssignedLinkedStaffId = null;

        let deletePreCreatedId = null;
        // Adopt an email-keyed doc whether or not it carries `preCreated`.
        //
        // A genuine placeholder has preCreated:true. But the "Fix email" button
        // in Settings > People used to RE-KEY a real, already-signed-in user's
        // doc from their auth uid to this same mangled-email id (and delete the
        // uid-keyed original), which left them orphaned: /users/{uid} missing,
        // so every sign-in re-runs this first-login branch. Requiring the
        // preCreated flag meant those users adopted NO role and silently
        // dropped to the read-mostly all_teachers default — a teacher quietly
        // losing their class. Adopting on presence alone heals them on next
        // sign-in. The self-create is still gated server-side by
        // selfBootstrapRoleOk(), so this cannot be used to claim a role the
        // rules wouldn't already grant.
        if (preCreatedSnap.exists()) {
          const pcd = preCreatedSnap.data();
          preAssignedRole = pcd.role || null;
          preAssignedCode = pcd.staffCode || null;
          preAssignedScopeYearLevels = Array.isArray(pcd.scopeYearLevels) ? pcd.scopeYearLevels : [];
          preAssignedScopeStudentIds = Array.isArray(pcd.scopeStudentIds) ? pcd.scopeStudentIds : [];
          preAssignedLinkedStaffId = pcd.linkedStaffId || null;
          // Delete AFTER we've created /users/{uid} — the Firestore rule verifies
          // this pre-created doc to authorise a privileged self-created role.
          deletePreCreatedId = preCreatedId;
        }

        // A pre-created (Leadership-assigned) role wins. Otherwise everyone
        // self-registers as the read-mostly default and Leadership promotes them
        // in Settings → People. NOTE: a self-registering user can no longer claim
        // Owner just by being "first" — that was a privilege-escalation hole. The
        // school's Owner is the vendor master account (or set at provisioning).
        const usersSnap = await getDocs(collection(db, 'users'));
        const realUsers = usersSnap.docs.filter(d => !d.data().preCreated);
        if (realUsers.length === 0 && !preAssignedRole) {
          currentUserRole = 'all_teachers';
          currentUserYearLevel = null;
        } else if (preAssignedRole) {
          // Admin pre-created this user with a role. (Used to require a
          // staff code too — staff codes are gone, but pre-creation via
          // Settings → People still carries the role.)
          currentUserRole = normaliseRole(preAssignedRole);
          currentUserYearLevel = null;
        } else {
          // Allowlisted but no pre-assigned role. Default to all_teachers;
          // leadership can upgrade them in Settings, People.
          currentUserRole = 'all_teachers';
          currentUserYearLevel = null;
        }

        currentUserPermissions = {};
        currentUserScopeYearLevels = preAssignedScopeYearLevels;
        currentUserScopeStudentIds = preAssignedScopeStudentIds;
        await setDoc(userRef, {
          email: user.email, displayName: user.displayName,
          role: currentUserRole,
          staffCode: preAssignedCode || null,
          yearLevel: currentUserYearLevel || null,
          scopeYearLevels: currentUserScopeYearLevels,
          scopeStudentIds: currentUserScopeStudentIds,
          customPermissions: {},
          createdAt: serverTimestamp(), lastLogin: serverTimestamp(),
        });
        // Now that the real /users/{uid} doc exists (the create rule verified the
        // pre-created doc to authorise the role), remove the pre-created placeholder.
        if (deletePreCreatedId) {
          try {
            const { deleteDoc: delDoc } = await import('firebase/firestore');
            await delDoc(doc(db, 'users', deletePreCreatedId));
          } catch { /* best-effort - the placeholder is harmless if it lingers */ }
        }
      }
    }
    // Get custom claims from token (set by Cloud Function). Master users
    // keep 'owner' even if claims say otherwise — prevents a stale claim
    // from demoting Liz / Harry.
    const masterEmails = ['harry@easyclarity4growth.com', 'elizabeth@easyclarity4growth.com'];
    // Force a token refresh so role/scope custom claims the syncUserClaims Cloud
    // Function just materialised (role change, new scope, first-login backfill)
    // land in the token THIS session — Storage rules gate on request.auth.token.*
    // and a cached token would otherwise carry stale/absent claims for up to 1h.
    //
    // CRITICAL (login-hang fix): getIdTokenResult(true) does a network round-trip
    // that can HANG indefinitely when the browser blocks the Firebase auth
    // iframe / 3rd-party cookies — the classic "login just spins and never lets
    // me in". It must NEVER block sign-in: fresh claims are only needed for
    // Storage uploads (Firestore rules read the /users doc for role, so the app
    // works regardless). So we time-box it and fall back to the CACHED token; a
    // stale-but-present token still signs the user in.
    let token;
    _authStage('refreshing-token');
    try {
      token = await withTimeout(user.getIdTokenResult(true), 8000, 'token-refresh-timeout');
    } catch (e) {
      console.warn('Forced token refresh slow/failed - using cached token so sign-in still completes:', e?.message || e);
      try { token = await withTimeout(user.getIdTokenResult(false), 5000, 'cached-token-timeout'); }
      catch { token = { claims: {} }; } // last resort: proceed; rules read the users doc
    }
    // First-login / just-backfilled race: syncUserClaims may set the role claim
    // a beat AFTER this refresh. If the /users doc says the user has a role but
    // the token still lacks the claim, materialise it via the callable — but
    // TIME-BOXED, so a slow/cold-starting function can't hang the whole login.
    if (!token.claims?.role && userSnap.exists() && userSnap.data()?.role) {
      try {
        const { httpsCallable } = await import('firebase/functions');
        await withTimeout(httpsCallable(functions, 'ensureMyClaims')(), 8000, 'ensureMyClaims-timeout');
        token = await withTimeout(user.getIdTokenResult(true), 6000, 'token-refresh-2-timeout');
      } catch (e) {
        // Non-fatal: the app still works (rules read the /users doc). The claim
        // will materialise on the next reload. Do NOT block sign-in on it.
        console.warn('claim self-heal (ensureMyClaims) skipped - proceeding with sign-in:', e?.message || e);
      }
    }
    // The /users DOC wins for the ROLE — the claims copy is only the fallback
    // for a failed doc read. Same reasoning as the permissions block below:
    // the doc is what Firestore rules actually enforce, while the token can
    // be STALE (warm sessions fall back to a cached token on slow school
    // wifi, and claim sync lags a role change). This line used to OVERRIDE
    // the doc role set above with the claims role — so a role change ran the
    // whole UI as the OLD role while the server enforced the new one
    // ("permissions had updated... now all my permissions are gone again",
    // 28-30 Aug 2026).
    if ((!userSnap.exists() || !userSnap.data()?.role)
        && token.claims.role && !masterEmails.includes(user.email)) {
      currentUserRole = normaliseRole(token.claims.role);
    }
    // The /users DOC wins for custom permissions — never the token.
    //
    // The token copy is the less trustworthy of the two and it was overriding
    // the good one. It can be STALE: a warm session refreshes the token with an
    // 8s timeout and falls back to the cached one on a slow school network, so a
    // permission granted days ago may still not be in the browser (St Brigid's
    // speech pathologist could not see Interventions for a week — her session
    // was carrying 5 August claims). It can also be TRUNCATED: custom claims are
    // capped at 1000 bytes and hers already sits at 975, so the next permission
    // added would silently fail to fit.
    //
    // The doc has neither limit and it is what the Firestore rules actually
    // enforce, so trusting it makes the UI agree with the server rather than
    // diverge from it. Claims stay as a fallback for the case where the doc read
    // failed, and remain the source for Storage rules, which can only see the
    // token.
    const docPerms = userSnap.exists() ? userSnap.data()?.customPermissions : null;
    if (docPerms && Object.keys(docPerms).length) currentUserPermissions = docPerms;
    else if (token.claims.permissions) currentUserPermissions = token.claims.permissions;
    } catch (err) {
      console.error('Auth setup error:', err);
      if (err?.code === 'timeout') {
        // We never reached Firestore — this is a connection failure, NOT a
        // first-login bootstrap. Falling through to the 'owner' fallback below
        // would paint a full admin shell over an app with no data, which is how
        // a network problem ends up looking like a broken app. Record it and
        // keep least privilege; main.js surfaces the real diagnostic.
        _authStage('firestore-unreachable');
        bootConnectError = err;
        currentUserRole = 'all_teachers';
      } else {
        // If permissions error on first login, the rules may not allow bootstrap yet
        // Fall back to basic authenticated state
        currentUserRole = 'owner'; // First user fallback
      }
    }
  } else {
    currentUserRole = 'all_teachers';
    currentUserPermissions = {};
    currentUserYearLevel = null;
  }
  _authStage('starting-app');
  authListeners.forEach(cb => cb(currentUser, currentUserRole));
});

// Read-only console diagnostics (no behaviour): lets a live session answer
// "what role/permissions is the auth module actually holding right now?"
// without a debugger — the 30 Aug signature-feed hunt needed exactly this.
if (typeof window !== 'undefined') {
  window._nccdGate = {
    role: () => currentUserRole,
    perms: () => currentUserPermissions,
    sectionAccess: (k) => sectionAccess(k),
    canUseSignatureLibrary: () => canUseSignatureLibrary(),
  };
}
